import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useRef } from 'react';

import ChatClient, { PongEvent } from '../clients/chat-client';

const PING_INTERVAL_MS = 1000;
const CONNECTION_UNSTABLE_THRESHOLD_MS = 3000;
const CONNECTION_LOST_THRESHOLD_MS = 10000;

export const useChatClientHealthCheck = ({
  chatClient,
  enabled,
  onConnectionUnstable,
  onConnectionLost,
  onConnectionRecovered,
}: {
  chatClient: ChatClient | null;
  enabled: boolean;
  onConnectionUnstable?: () => void;
  onConnectionLost?: () => void;
  onConnectionRecovered?: () => void;
}) => {
  const pingSendTimes = useRef(new Map<string, number>());

  useEffect(() => {
    if (!enabled || !chatClient) {
      return;
    }

    let lastPongTime = Date.now();

    let isConnectionUnstable = false;
    let isConnectionLost = false;

    let pingInterval: NodeJS.Timeout | undefined;

    const handlePong = (event: PongEvent) => {
      lastPongTime = Date.now();

      const { pingId } = event;
      const pingSendTime = pingSendTimes.current.get(pingId);

      if (pingSendTime) {
        datadogRum.addDurationVital('alignerr-interview-api-ping-latency', {
          startTime: pingSendTime,
          duration: Date.now() - pingSendTime,
          context: {
            visibilityState: document.visibilityState,
          },
        });
        pingSendTimes.current.delete(pingId);
      }

      // Also log the latency of the pong event - the duration between when the
      // interview-api sends the pong and when it is received by us.
      datadogRum.addDurationVital('alignerr-interview-api-pong-latency', {
        startTime: event.timestamp,
        duration: Date.now() - event.timestamp,
        context: {
          visibilityState: document.visibilityState,
        },
      });

      if (isConnectionUnstable && !isConnectionLost) {
        recover();
      }
    };

    const recover = () => {
      isConnectionUnstable = false;
      onConnectionRecovered?.();
      if (!pingInterval) {
        startPinging();
      }
    };

    const startPinging = () => {
      pingInterval = setInterval(() => {
        let timeSinceLastPong = Infinity;

        try {
          const pingId = chatClient.ping();
          pingSendTimes.current.set(pingId, Date.now());
          timeSinceLastPong = Date.now() - lastPongTime;
        } catch (error) {
          console.error('Error pinging chat server:', error);
        }

        if (timeSinceLastPong >= CONNECTION_LOST_THRESHOLD_MS) {
          isConnectionLost = true;
          if (pingInterval) {
            clearInterval(pingInterval);
            pingInterval = undefined;
          }
          onConnectionLost?.();
        } else if (timeSinceLastPong >= CONNECTION_UNSTABLE_THRESHOLD_MS) {
          isConnectionUnstable = true;
          onConnectionUnstable?.();
        }
      }, PING_INTERVAL_MS);
    };

    chatClient.on('pong', handlePong);
    startPinging();

    return () => {
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = undefined;
      }
      chatClient.off('pong', handlePong);
    };
  }, [
    enabled,
    chatClient,
    onConnectionUnstable,
    onConnectionLost,
    onConnectionRecovered,
  ]);
};
